import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Clock, FileText, AlertCircle, Eye, Gavel } from "lucide-react";
import { formatWithTimezoneLabel, isDeadlinePassed } from "@/lib/timezone";

interface RFPInvitation {
  id: string;
  rfp_id: string;
  invite_token: string;
  hasProposal: boolean;
  rfps: {
    title: string;
    description: string;
    submission_deadline: string;
    auction_start: string;
    status: string;
    matter_name: string | null;
    region: string | null;
    team: string | null;
    min_bid: number | null;
    min_decrement: number | null;
  };
}

// Helper to capitalize words properly
const capitalizeWords = (str: string) => {
  return str.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase());
};

const SupplierActiveRFPs = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState<RFPInvitation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && user) {
      fetchActiveRFPs();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const fetchActiveRFPs = async () => {
    if (!user?.email) {
      setLoading(false);
      return;
    }

    try {
      const { data: allInvites, error: invitesError } = await supabase
        .from("supplier_invitations")
        .select(`
          id,
          rfp_id,
          invite_token,
          rfps!inner (
            title,
            description,
            submission_deadline,
            auction_start,
            status,
            matter_name,
            region,
            team,
            min_bid,
            min_decrement,
            deleted_by_buyer
          )
        `)
        .eq("supplier_email", user.email);

      if (invitesError) throw invitesError;

      // Get all proposal RFP IDs for this user
      const { data: userProposals } = await supabase
        .from("proposals")
        .select("rfp_id")
        .eq("user_id", user.id);
      
      const proposalRfpIds = new Set((userProposals || []).map((p: any) => p.rfp_id));
      const now = new Date();

      // Filter for active RFPs:
      // 1. Not deleted by buyer
      // 2. No proposal submitted AND open for submissions
      // 3. OR has proposal but auction hasn't started yet
      const activeInvites = (allInvites || []).filter((inv: any) => {
        if (!inv.rfps) return false;
        // Exclude deleted RFPs
        if (inv.rfps.deleted_by_buyer === true) return false;
        
        const hasProposal = proposalRfpIds.has(inv.rfp_id);
        const auctionStarted = new Date(inv.rfps.auction_start) <= now;
        
        // Include if no proposal and open for submissions/awaiting auction
        if (!hasProposal && (inv.rfps.status === "OPEN_FOR_SUBMISSIONS" || inv.rfps.status === "AWAITING_AUCTION")) {
          return true;
        }
        
        // Include if has proposal but auction hasn't started
        if (hasProposal && !auctionStarted && inv.rfps.status !== "AUCTION_ENDED" && inv.rfps.status !== "COMPLETED") {
          return true;
        }
        
        return false;
      }).map((inv: any) => ({
        ...inv,
        hasProposal: proposalRfpIds.has(inv.rfp_id)
      }));
      
      setInvitations(activeInvites);
    } catch (err) {
      console.error("Error fetching active RFPs:", err);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Active RFPs</h1>
          <p className="text-muted-foreground mt-2">RFPs awaiting your proposal or pending auction</p>
        </div>

        {invitations.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No active RFPs available</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {invitations.map((inv) => {
              const hasReverseAuction = inv.rfps.min_bid !== null || inv.rfps.min_decrement !== null;
              const auctionStarted = isDeadlinePassed(inv.rfps.auction_start);
              
              return (
                <Card key={inv.id} className="hover:shadow-lg transition-shadow flex flex-col">
                  <CardHeader>
                    <CardTitle className="text-lg">{inv.rfps.matter_name || inv.rfps.title}</CardTitle>
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="secondary">{inv.rfps.status.replace(/_/g, " ")}</Badge>
                      {inv.hasProposal && (
                        <Badge className="bg-green-500">Proposal Submitted</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4 flex-1 flex flex-col">
                    <p className="text-sm text-muted-foreground line-clamp-2 flex-1">
                      {inv.rfps.description}
                    </p>
                    {(inv.rfps.region || inv.rfps.team) && (
                      <div className="flex flex-wrap gap-2 text-xs">
                        {inv.rfps.region && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">
                            {capitalizeWords(inv.rfps.region)}
                          </span>
                        )}
                        {inv.rfps.team && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded">
                            {capitalizeWords(inv.rfps.team)}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4" />
                      <span>
                        {hasReverseAuction 
                          ? `Auction: ${formatWithTimezoneLabel(inv.rfps.auction_start)}`
                          : `Due: ${formatWithTimezoneLabel(inv.rfps.submission_deadline)}`
                        }
                      </span>
                    </div>
                    {!inv.hasProposal && isDeadlinePassed(inv.rfps.submission_deadline) && (
                      <Badge variant="destructive" className="text-xs">Submissions closed</Badge>
                    )}
                    <div className="mt-auto pt-4">
                      {inv.hasProposal ? (
                        <Button
                          onClick={() => navigate(`/supplier/rfp/${inv.rfp_id}/view/${inv.invite_token}`)}
                          variant="outline"
                          className="w-full"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Proposal
                        </Button>
                      ) : (
                        <Button
                          onClick={() => navigate(`/rfp/${inv.rfp_id}/respond/${inv.invite_token}`)}
                          className="w-full"
                          disabled={isDeadlinePassed(inv.rfps.auction_start)}
                        >
                          Submit Proposal
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierActiveRFPs;
